import { useState } from 'react';
import { Box, Card, Typography, Button, CircularProgress, Link } from '@mui/material';

import useSWRMutation from "swr/mutation";

import { MuiOtpInput } from 'mui-one-time-password-input';

import { Email, validateEmail } from '../input/Email';
import { Password, validatePassword, PasswordCriteria } from '../input/Password';

import { useClient } from "../../../context/ClientProvider";
import { useUtils } from "../../../context/UtilsProvider";
import { useAuth } from '../../../context/AuthProvider';


/** 
* Forgot password handling card
*/
export default function ForgotPasswordCard() {

  const forgotPasswordStates = {
    enterEmailAddress: "enterEmailAddress",
    enterNewPassword: "enterNewPassword",
    enterOtp: "enterOtp"
  };
  const [ forgotPasswordState, setForgotPasswordState ] = 
    useState(forgotPasswordStates.enterEmailAddress);

  const [ otp, setOtp ] = useState("");

  const handleOtpChange = (newValue) => {
    setOtp(newValue);
  };

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();

  const {
    formError,
    setFormError,
    formState,
    setFormState,
    createAlert,
    alertSeverityTypes,
  } = useUtils();

  const {
    postForgotPassword,
    postConfirmPassword,
    authStates,
    setAuthState,
  } = useAuth();

  const {
    trigger: forgotPasswordTrigger,
    isMutating: forgotPasswordIsMutating
  } = useSWRMutation("/auth/forgot-password", postForgotPassword);

  const {
    trigger: confirmPasswordTrigger,
    isMutating: confirmPasswordIsMutating
  } = useSWRMutation("/auth/confirm-password", postConfirmPassword);

  const handleForgotPassword = async () => {
    if (validateEmail(formState.email)) {
      try {
        const response = await forgotPasswordTrigger({
          username: formState.email
        });
        console.info("post forgot password response: ", response);
        if (response && response.CodeDeliveryDetails.DeliveryMedium === "EMAIL") {
          setForgotPasswordState(forgotPasswordStates.enterNewPassword); 
        }
      } catch (error) {
        const message = "Email address not found. Please try again";
        console.error("post forgot password error", error);
        createAlert(message, alertSeverityTypes.error);
      }
    } else {
      createAlert("Please enter a valid email address", alertSeverityTypes.error);
    };
  };

  const handleNewPassword = async () => {
    if (validatePassword(formState.password)) {
      setForgotPasswordState(forgotPasswordStates.enterOtp);
    } else {
      createAlert("Password is invalid. Please try again", alertSeverityTypes.error);
    };
  };

  const resendOtp = async () => {
    try {
      const response = await forgotPasswordTrigger({
        username: formState.email
      });
      console.info("post forgot password response: ", response);
      createAlert("OTP successfully resent. Please try again.", alertSeverityTypes.success);
    } catch (error) {
      const message = "There was a problem sending your OTP code. Please try again";
      console.error("post forgot password error", error);
      createAlert(message, alertSeverityTypes.error);
    }
  };

  const handleOtpComplete = async () => {
    try {
      const confirmPasswordResponse = await confirmPasswordTrigger({
        username: formState.email,
        password: formState.password,
        otp: otp
      });
      console.info("post confirm password response: ", confirmPasswordResponse);
      if (confirmPasswordResponse.hasOwnProperty("errorMessage")) {
        throw new Error;
      }
      setFormError(null);
      createAlert("Password reset successful. Please login with your new password.", alertSeverityTypes.success);
      setAuthState(authStates.signedOut);
    } catch (error) {
      setOtp("");
      createAlert("Incorrect OTP code entered. Please try again.", alertSeverityTypes.error);
    }
  };

  return clientConfig && (
    <Card
      id="forgot-password-card"
      sx={{
        maxWidth: { md: "80%" },
        mx: "auto",
        mb: "350px",
        px: { xs: 2, sm: 3 },
        py: 3,
        borderRadius: "0",
        position: "relative",
        bottom: { xs: "auto", md: "300px" },
        boxShadow: "-9px 10px 4px -1px rgba(0, 0, 0, 0.2)",
      }}
    >
      {forgotPasswordState === forgotPasswordStates.enterEmailAddress &&
        <>
          <Box sx={{ mb: 4 }}>
            <Typography
              component='h1'
              variant='h5'
              sx={{ mb: 1, fontWeight: "bold" }}
            >
              Forgotten your password?
            </Typography>
            <Typography component='h2' variant='p'>
              Just enter your email address and click 'Reset Password'. We'll send a one-time passcode
              to your email address. Enter the code to set up a new password & get back into your account securely.
            </Typography>
          </Box>

          <Box sx={{ width: { xs: "100%", md: "50%" } }}>
            <Email
              label="Email address"
              inputLabel="Enter your Email address"
              formState={formState}
              setFormState={setFormState}
              setFormError={setFormError}
            />
          </Box>

          {/* Button section */}
          <Box sx={{ mt: 5 }}>
            <Button
              type='button'
              onClick={handleForgotPassword}
              variant='contained'
              sx={{
                borderRadius: clientConfig.layout.buttonBorderRadius,
                width: { xs: "70%", sm: "25%" },
                backgroundColor: clientConfig.layout.primaryButtonBackgroundColour,
                color: clientConfig.layout.primaryButtonColour,
                "&:hover": {
                  backgroundColor: clientConfig.layout.hoverPrimaryButtonBackgroundColour,
                  color: clientConfig.layout.hoverPrimaryButtonColour,
                }
              }}
            >
              Reset Password
            </Button>
          </Box>
          {/* End of button section */}
        </>
      }

      {forgotPasswordState === forgotPasswordStates.enterNewPassword &&
        <>
          <Box sx={{ mb: 4 }}>
            <Typography
              component='h1'
              variant='h5'
              sx={{ mb: 1, fontWeight: "bold" }}
            >
              Forgotten your password?
            </Typography>
            <Typography component='h2' variant='p'>
              Create your new password by entering it below. Make sure it's strong & secure. Once confirmed, you'll 
              be ready to log back in.
            </Typography>
          </Box>

          <Box sx={{ width: { xs: "100%", md: "50%" } }}>
            <Password
              label="Password"
              inputLabel="Enter your password"
              formState={formState}
              setFormState={setFormState}
              setFormError={setFormError}
            />
          </Box>

          <Box sx={{ mt: 4 }}>
            <PasswordCriteria />
          </Box>

          {/* Button section */}
          <Box sx={{ mt: 4 }}>
            <Button
              type='button'
              onClick={handleNewPassword}
              variant='contained'
              sx={{
                borderRadius: clientConfig.layout.buttonBorderRadius,
                width: { xs: "70%", sm: "25%" },
                backgroundColor: clientConfig.layout.primaryButtonBackgroundColour,
                color: clientConfig.layout.primaryButtonColour,
                "&:hover": {
                  backgroundColor: clientConfig.layout.hoverPrimaryButtonBackgroundColour,
                  color: clientConfig.layout.hoverPrimaryButtonColour,
                }
              }}
            >
              Confirm
            </Button>
          </Box>
          {/* End of button section */}

        </>
      }

      {forgotPasswordState === forgotPasswordStates.enterOtp &&
        <>
          <Box sx={{ mb: 4 }}>
            <Typography
              component='h1'
              variant='h5'
              sx={{ mb: 1, fontWeight: "bold" }}
            >
              Forgotten your password?
            </Typography>
            <Typography component='h2' variant='p'>
              Enter the one-time passcode (OTP) we've sent to your registered email to verify your request. 
              This code is valid for a limited time, so be sure to use it soon. If you haven't received it, check 
              your spam folder or request a new code. 
            </Typography>
          </Box>

          <Box sx={{ mb: 1 }}>
            <MuiOtpInput
              value={otp}
              onChange={handleOtpChange}
              length={6}
              autoFocus
              validateChar={(char) => !isNaN(char)}
              sx={{
                '& .MuiInputBase-input': {
                  width: {xs: '10px', sm: '20px', md: '25px'},
                  height: {xs: '10px', sm: '20px', md: '25px'},
                  fontSize: {xs: '14px', md: '30px'},
                  borderRadius: "50px"
                }
              }}
            />
          </Box>

          <Link
            component="button"
            variant="body2"
            onClick={resendOtp}
          >
            Resend OTP
          </Link>

          {/* Button section */}
          <Box sx={{ mt: 4, mb: 5 }}>
            <Button
              type='button'
              onClick={handleOtpComplete}
              variant='contained'
              sx={{
                borderRadius: clientConfig.layout.buttonBorderRadius,
                width: { xs: "70%", sm: "25%" },
                backgroundColor: clientConfig.layout.primaryButtonBackgroundColour,
                color: clientConfig.layout.primaryButtonColour,
                "&:hover": {
                  backgroundColor: clientConfig.layout.hoverPrimaryButtonBackgroundColour,
                  color: clientConfig.layout.hoverPrimaryButtonColour,
                }
              }}
            >
              Confirm OTP
            </Button>
          </Box>
          {/* End of button section */}
        </>
      }
    
      {/* Error message and loading section */}
      <Box sx={{ position: "relative", mt: 1 }}>
        {(forgotPasswordIsMutating || confirmPasswordIsMutating) && (
          <CircularProgress
            size={24}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              marginTop: "-12px",
              marginLeft: "-12px",
            }}
          />
        )}
        <Typography
          sx={{ ml: 1, display: "inline-block", mb: { xs: 2 } }}
          color='error'
        >
          {formError && `* ${formError}`}
        </Typography>
      </Box>
      {/* End of error message and loading section */}

    </Card>
  )
};